import { Vec2 } from './vec';
import type { TowerPoisonSpec } from './towerPoison';
import type { EnemyType } from './types';

/**
 * Structural HP every tower gains the moment an Ember Barrage carrier (King
 * Ember) reaches the field. Towers have no HP at all before that — the field is
 * undefined — which is what keeps the bar hidden for the rest of the campaign.
 */
export const TOWER_MAX_HP = 3;

/** Peak height (px) a fireball arcs above the straight line to its target. */
const ARC_HEIGHT = 90;

/**
 * One of King Ember's fireballs, in flight. It carries its own travel time
 * rather than landing instantly so the damage and the visual can never
 * disagree: the tower loses HP on the tick the ball arrives, not when it left.
 */
export interface FireballState {
  id: number;
  /** Launch point — the boss's position at the moment he threw it. */
  from: Vec2;
  /** Landing point — the target tower's plot. */
  to: Vec2;
  /** Tower this ball is aimed at; if it is gone on arrival the ball fizzles. */
  targetTowerId: number;
  /** Seconds in flight so far. */
  elapsed: number;
  /** Total flight time in seconds (EnemySpec.towerFire.travel). */
  duration: number;
  /** HP taken off the target on arrival. */
  damage: number;
  /** Current interpolated position along the arc — the render layer reads this. */
  pos: Vec2;
  /** Atlas key to draw instead of the fireball (see EnemySpec.towerFire). */
  projectile?: string;
  /** Draw a fading beam from `from` to `to` instead of a flying object. */
  beam?: boolean;
  /**
   * The shot FELL from above its target rather than being thrown across the
   * field, so it travels in a straight line with no lob (see
   * EnemySpec.towerFire.fromSky).
   */
  fromSky?: boolean;
  /**
   * The enemy that threw it, so the render layer can play that enemy's swing
   * animation on the frame the shot appears. Undefined for a hand-built state.
   */
  ownerId?: number;
  /** Draw the thrown sprite this many times normal size (see towerFire.scale). */
  scale?: number;
  /**
   * Nothing flies: the carrier swung (The Shadow Reaper's scythe), and the
   * render flashes "KO" over the tower when it lands (see towerFire.swing).
   */
  swing?: boolean;
  /**
   * A possessed tower threw it at another tower (EnemySpec.ghosting) — the
   * render draws it as a purple spirit from the tower, not from an enemy.
   */
  ghosted?: boolean;
  /** The shot poisons its target on landing (EnemySpec.towerFire.poison). */
  poison?: TowerPoisonSpec;
  /**
   * The shot shroomnitizes its target on landing (EnemySpec.towerFire.possess),
   * `by` being the thrower's type, for the render's tint.
   */
  possess?: {
    by: EnemyType;
    hits: number;
    damage: number;
    first: number;
    gap: number;
    projectile: string;
  };
}

export function createFireball(
  id: number,
  from: Vec2,
  to: Vec2,
  targetTowerId: number,
  duration: number,
  damage: number,
  projectile?: string,
  beam?: boolean,
  fromSky?: boolean,
  ownerId?: number,
  scale?: number,
): FireballState {
  return {
    id,
    from: { ...from },
    to: { ...to },
    targetTowerId,
    elapsed: 0,
    duration,
    damage,
    pos: { ...from },
    ...(projectile ? { projectile } : {}),
    ...(beam ? { beam } : {}),
    ...(fromSky ? { fromSky } : {}),
    ...(ownerId !== undefined ? { ownerId } : {}),
    ...(scale !== undefined ? { scale } : {}),
  };
}

/**
 * Advance one fireball along its arc.
 *
 * Returns true while it is still in flight and false on the tick it lands —
 * the same in-flight/landed contract `updateProjectile` uses, so the caller
 * filters survivors and resolves the impact in one pass.
 */
export function updateFireball(f: FireballState, dt: number): boolean {
  f.elapsed += dt;
  const t = f.duration > 0 ? Math.min(1, f.elapsed / f.duration) : 1;
  // A falling spear comes STRAIGHT down: the lob is for a thrown thing, and an
  // arc on a vertical drop reads as a bug.
  const arc = f.fromSky ? 0 : ARC_HEIGHT;
  f.pos = {
    x: f.from.x + (f.to.x - f.from.x) * t,
    // 4·t·(1−t) peaks at 1 halfway along, so the ball lobs rather than beelines.
    y: f.from.y + (f.to.y - f.from.y) * t - arc * 4 * t * (1 - t),
  };
  return f.elapsed < f.duration;
}
